import React, { useState } from 'react';
import { 
  FaUserCircle, 
  FaTasks, 
  FaGraduationCap, 
  FaCheckDouble, 
  FaUniversity, 
  FaBriefcase,
  FaSlidersH,
  FaCode,
  FaLaptopCode
} from 'react-icons/fa';
import { MdOutlineRssFeed } from 'react-icons/md';

// Explicitly aligned with your accurate component file naming layout
import LiveResumeHub from '../LiveResumeHub/LiveResumeHub';
import DailyLoggingEngine from '../DailyLoggingEngine/DailyLoggingEngine';
import VerificationLoop from '../VerificationLoop/VerificationLoop';
import LearningEngineView from '../LearningEngineView/LearningEngineView';
import CompetencyMatrix from '../CompetencyMatrix/CompetencyMatrix';
import ProofOfWorkVault from '../ProofOfWorkVault/ProofOfWorkVault';
import CompanyExplorer from '../CompanyExplorer/CompanyExplorer';
import SocialActivityPulse from '../SocialActivityPulse/SocialActivityPulse';
import JobsCenter from '../JobsCenter/JobsCenter';

export default function DemoApp() {
  const [activeScreen, setActiveScreen] = useState('profile');
  const [activeSprintSubject, setActiveSprintSubject] = useState(null);

  // Dynamic routing orchestrator for internal cross-screen jumps
  const handleTriggerSyllabusSprint = (subjectName) => {
    setActiveSprintSubject(subjectName);
    setActiveScreen('syllabus');
  };

  const handleTriggerVerificationQuiz = () => {
    setActiveScreen('quiz');
  };

  // Sidebar navigation configuration mapping keys to views
  const navigationRoutes = [
    { id: 'profile', name: '1. Live Resume Hub', icon: <FaUserCircle />, section: 'Builder Core' },
    { id: 'logger', name: '2. Daily Logging Engine', icon: <FaTasks />, section: 'Builder Core' },
    { id: 'quiz', name: '3. Verification Loop', icon: <FaCheckDouble />, section: 'Verification Loops' },
    { id: 'syllabus', name: '4. Learning Engine View', icon: <FaGraduationCap />, section: 'Verification Loops' },
    { id: 'social', name: '8. Social Activity Pulse', icon: <MdOutlineRssFeed />, section: 'Verification Loops' },
    { id: 'jobs', name: '9. Jobs Center', icon: <FaBriefcase />, section: 'Marketplace Integration' },
    { id: 'admin', name: '5. Competency Matrix', icon: <FaSlidersH />, section: 'Enterprise & Institutional' },
    { id: 'vault', name: '6. Proof Of Work Vault', icon: <FaCode />, section: 'Enterprise & Institutional' },
    { id: 'pilot', name: '7. Company Explorer', icon: <FaUniversity />, section: 'Enterprise & Institutional' },
  ];

  // Component Renderer Router Switch
  const renderActiveScreenComponent = () => {
    switch(activeScreen) {
      case 'profile':
        return <LiveResumeHub onTriggerVerification={handleTriggerVerificationQuiz} />;
      case 'logger':
        return <DailyLoggingEngine onLogSuccess={handleTriggerVerificationQuiz} />;
      case 'quiz':
        return <VerificationLoop onQuizComplete={() => setActiveScreen('profile')} />;
      case 'syllabus':
        return <LearningEngineView initialSubject={activeSprintSubject} />;
      case 'admin':
        return <CompetencyMatrix />;
      case 'vault':
        return <ProofOfWorkVault />;
      case 'pilot':
        return <CompanyExplorer />;
      case 'social':
        return <SocialActivityPulse onGenerateSyllabus={handleTriggerSyllabusSprint} />;
      case 'jobs':
        return <JobsCenter onGenerateCourse={handleTriggerSyllabusSprint} />;
      default:
        return <LiveResumeHub />;
    }
  };

  return (
    <div style={styles.appShell}>
      {/* Background Decorative Theme Elements */}
      <div style={styles.glowingOrbPink} />
      <div style={styles.glowingOrbCyan} />

      {/* Persistent App Sidebar */}
      <aside style={styles.sidebar}>
        <div style={styles.brandContainer}>
          <div style={styles.logoBox}>
            <FaLaptopCode style={styles.logoIcon} />
          </div>
          <div style={styles.brandText}>
            <h1 style={styles.appName}>HQVibe</h1>
            <span style={styles.appEnvironment}>Live Resume Engine v1.0</span>
          </div>
        </div>

        <nav style={styles.navMenu}>
          {['Builder Core', 'Verification Loops', 'Marketplace Integration', 'Enterprise & Institutional'].map((section) => (
            <div key={section} style={styles.navSection}>
              <h3 style={styles.navSectionTitle}>{section}</h3>
              {navigationRoutes
                .filter(route => route.section === section)
                .map((route) => {
                  const isSelected = activeScreen === route.id;
                  return (
                    <button
                      key={route.id}
                      onClick={() => setActiveScreen(route.id)}
                      style={{ 
                        ...styles.navLinkButton, 
                        ...(isSelected ? styles.activeNavLinkButton : {}) 
                      }}
                    >
                      <span style={{ 
                        ...styles.routeIcon, 
                        ...(isSelected ? styles.activeRouteIcon : {}) 
                      }}>
                        {route.icon}
                      </span>
                      <span style={styles.routeName}>{route.name}</span>
                    </button>
                  );
                })}
            </div>
          ))}
        </nav>

        <div style={styles.sidebarFooter}>
          <div style={styles.pilotBadge}>
            <span style={styles.pilotBadgeDot} />
            UNLV Pilot Program Mode Active
          </div>
        </div>
      </aside>

      {/* Core Dynamic Content Viewing Frame */}
      <main style={styles.mainViewport}>
        <header style={styles.viewportHeaderBar}>
          <div style={styles.headerLeft}>
            <span style={styles.environmentTag}>DEMO ENVIRONMENT</span>
            <h2 style={styles.activeRouteTitle}>
              {navigationRoutes.find(r => r.id === activeScreen)?.name}
            </h2>
          </div>
          <div style={styles.userProfilePill}>
            <div style={styles.userAvatarPlaceholder}>KD</div>
            <div style={styles.userPillMeta}>
              <span style={styles.userPillName}>Kandarp Dave</span>
              <span style={styles.userPillRole}>Solo Founder</span>
            </div>
          </div>
        </header>

        {/* View Layout Container */}
        <div style={styles.viewContentWrapper}>
          {renderActiveScreenComponent()}
        </div>
      </main>
    </div>
  );
}

const styles = {
  appShell: {
    display: 'flex',
    width: '100vw',
    height: '100vh',
    backgroundColor: '#07090e',
    color: '#c9d1d9',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif',
    overflow: 'hidden',
    position: 'relative'
  },
  glowingOrbPink: {
    position: 'absolute',
    top: '-150px',
    left: '-150px',
    width: '500px',
    height: '500px',
    borderRadius: '50%',
    background: 'radial-gradient(circle, rgba(219,39,119,0.1) 0%, rgba(0,0,0,0) 70%)',
    zIndex: 0,
    pointerEvents: 'none'
  },
  glowingOrbCyan: {
    position: 'absolute',
    bottom: '-150px',
    right: '-150px',
    width: '600px',
    height: '600px',
    borderRadius: '50%',
    background: 'radial-gradient(circle, rgba(6,182,212,0.08) 0%, rgba(0,0,0,0) 70%)',
    zIndex: 0,
    pointerEvents: 'none'
  },
  sidebar: {
    width: '320px',
    backgroundColor: 'rgba(22, 27, 34, 0.7)',
    backdropFilter: 'blur(16px)',
    borderRight: '1px solid rgba(48, 54, 61, 0.6)',
    display: 'flex',
    flexDirection: 'column',
    zIndex: 10,
    height: '100%'
  },
  brandContainer: {
    padding: '24px',
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    borderBottom: '1px solid rgba(48, 54, 61, 0.4)'
  },
  logoBox: {
    width: '36px',
    height: '36px',
    borderRadius: '8px',
    background: 'linear-gradient(135deg, #db2777 0%, #4f46e5 100%)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxShadow: '0 0 15px rgba(219, 39, 119, 0.3)'
  },
  logoIcon: {
    color: '#ffffff',
    fontSize: '18px'
  },
  brandText: {
    display: 'flex',
    flexDirection: 'column'
  },
  appName: {
    fontSize: '18px',
    fontWeight: '800',
    color: '#ffffff',
    margin: 0,
    letterSpacing: '0.5px'
  },
  appEnvironment: {
    fontSize: '11px',
    color: '#8b949e',
    fontWeight: '500'
  },
  navMenu: {
    flex: 1,
    overflowY: 'auto',
    padding: '20px 16px',
    display: 'flex',
    flexDirection: 'column',
    gap: '20px'
  },
  navSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px'
  },
  navSectionTitle: {
    fontSize: '11px',
    fontWeight: '700',
    color: '#8b949e',
    textTransform: 'uppercase',
    letterSpacing: '1px',
    margin: '0 0 6px 8px'
  },
  navLinkButton: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    backgroundColor: 'transparent',
    border: '1px solid transparent',
    borderRadius: '6px',
    padding: '10px 12px',
    color: '#8b949e',
    fontSize: '13px',
    fontWeight: '500',
    textAlign: 'left',
    cursor: 'pointer',
    transition: 'all 0.15s ease'
  },
  activeNavLinkButton: {
    backgroundColor: 'rgba(79, 70, 229, 0.15)',
    border: '1px solid rgba(79, 70, 229, 0.3)',
    color: '#ffffff',
  },
  routeIcon: {
    fontSize: '14px',
    color: '#8b949e',
    display: 'flex',
    alignItems: 'center'
  },
  activeRouteIcon: {
    color: '#818cf8'
  },
  routeName: {
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis'
  },
  sidebarFooter: {
    padding: '16px',
    borderTop: '1px solid rgba(48, 54, 61, 0.4)'
  },
  pilotBadge: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    backgroundColor: 'rgba(6, 182, 212, 0.06)',
    border: '1px solid rgba(6, 182, 212, 0.15)',
    borderRadius: '6px',
    padding: '10px 12px',
    fontSize: '12px',
    color: '#22d3ee',
    fontWeight: '500'
  },
  pilotBadgeDot: {
    width: '6px',
    height: '6px',
    borderRadius: '50%',
    backgroundColor: '#22d3ee',
    boxShadow: '0 0 8px #22d3ee'
  },
  mainViewport: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    zIndex: 5
  },
  viewportHeaderBar: {
    height: '70px',
    backgroundColor: 'rgba(13, 17, 23, 0.5)',
    backdropFilter: 'blur(12px)',
    borderBottom: '1px solid rgba(48, 54, 61, 0.5)',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '0 32px',
    flexShrink: 0
  },
  headerLeft: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px'
  },
  environmentTag: {
    fontSize: '9px',
    fontWeight: '700',
    color: '#db2777',
    letterSpacing: '0.5px'
  },
  activeRouteTitle: {
    fontSize: '16px',
    fontWeight: '600',
    color: '#f0f6fc',
    margin: 0
  },
  userProfilePill: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    backgroundColor: '#161b22',
    border: '1px solid #30363d',
    padding: '6px 14px 6px 8px',
    borderRadius: '20px'
  },
  userAvatarPlaceholder: {
    width: '28px',
    height: '28px',
    borderRadius: '50%',
    background: 'linear-gradient(135deg, #4f46e5 0%, #06b6d4 100%)',
    color: '#ffffff',
    fontSize: '11px',
    fontWeight: '700',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  userPillMeta: {
    display: 'flex',
    flexDirection: 'column'
  },
  userPillName: {
    fontSize: '12px',
    fontWeight: '600',
    color: '#f0f6fc'
  },
  userPillRole: {
    fontSize: '10px',
    color: '#8b949e',
    lineHeight: 1
  },
  viewContentWrapper: {
    flex: 1,
    overflowY: 'auto',
    backgroundColor: 'transparent'
  }
};